import React, {
  FC,
  FormEvent as ReactFormEvent,
  useCallback,
  useRef,
} from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { preventedDefault } from '../../utils/events';
import { useCreateUserMutation } from '../../hooks/data/use-create-user-mutation';
import { writeStorage } from '@rehooks/local-storage';
import {
  LOCALSTORAGE_CLIENT_ID,
  LOCALSTORAGE_CLIENT_SECRET,
} from '../../constants';
import { BadRequestError } from '../../api/errors/BadRequestError';
import { NotAuthorizedError } from '../../api/errors/NotAuthorizedError';
import { UnexpectedError } from '../../api/errors/UnexpectedError';

interface CredentialsModalProps {
  isVisible: boolean;
}

export const CredentialsModal: FC<CredentialsModalProps> = ({ isVisible }) => {
  const formRef = useRef<HTMLFormElement>(null);
  const idInputRef = useRef<HTMLInputElement>(null);
  const secretInputRef = useRef<HTMLInputElement>(null);

  const {
    mutateAsync: createUser,
    error: createUserError,
    reset: resetCreateUser,
    status: createUserStatus,
  } = useCreateUserMutation();

  const handleFormSubmit = preventedDefault<ReactFormEvent<HTMLFormElement>>(
    () => {
      const clientId = idInputRef.current?.value;
      const clientSecret = secretInputRef.current?.value;

      if (clientId && clientSecret) {
        createUser({ clientId, clientSecret })
          .then(() => {
            writeStorage(LOCALSTORAGE_CLIENT_ID, clientId);
            writeStorage(LOCALSTORAGE_CLIENT_SECRET, clientSecret);
          })
          .catch(() => void 0); // silent catch
      }
    },
  );

  const handleOnChange = useCallback(() => {
    if (!!createUserError) {
      resetCreateUser();
    }
  }, [createUserError, resetCreateUser]);

  return (
    <Modal show={isVisible} backdrop="static" keyboard={false}>
      <Modal.Header>
        <Modal.Title>Application credentials</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert
          dismissible
          show={!!createUserError}
          variant="danger"
          onClose={resetCreateUser}
          transition={false}
        >
          {createUserError instanceof NotAuthorizedError &&
            'Your application is not authorized to create events. Please, contact the support.'}
          {createUserError instanceof BadRequestError &&
            'Provided credentials are invalid, please check them and try one more time.'}
          {createUserError instanceof UnexpectedError &&
            'Something went wrong. Please, retry later'}
        </Alert>
        <Form onSubmit={handleFormSubmit} ref={formRef}>
          <Form.Group controlId="client-id">
            <Form.Label>Client id</Form.Label>
            <Form.Control
              ref={idInputRef}
              type="text"
              placeholder="Enter the client id"
              onChange={handleOnChange}
            />
          </Form.Group>

          <Form.Group controlId="client-secret">
            <Form.Label>Client secret</Form.Label>
            <Form.Control
              ref={secretInputRef}
              type="password"
              placeholder="Enter the client secret"
              onChange={handleOnChange}
            />
          </Form.Group>
          <Button
            type="submit"
            variant="primary"
            disabled={createUserStatus === 'loading'}
          >
            Enter
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};
